import { queryOptions, useQuery } from "@tanstack/react-query";
import { useRequiredParams } from "~utils/url";
import { ApiResponse, ApiResponsePaginated } from "~api/api-response.model";
import { useSuspenseQueryDeferred } from "~utils/query-hooks";
import { assetsApiRequest } from "../api/api-request";
import { QueryOptions } from "../utils/consts";
import { PointGroup, PointGroupResponse } from "./point-group.model";

type PointGroupListParams = {
  companyId: number;
  page: number;
  pageSize: number;
  search?: string;
  withValues: boolean;
  graphicId?: number;
  graphicLink?: string;
};

type PointGroupsByIdsParams = {
  companyId: number;
  groupIds: number[];
  withValues: boolean;
};

export const pointGroupQueries = {
  list: (params: PointGroupListParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: [
        options?.customQueryKey ? options.customQueryKey : "point-groups",
        params,
      ],
      queryFn: ({ signal }) => fetchPointGroups(params, signal),
      staleTime: 60 * 1000, // consider stale after 1 minute
    }),
  byIds: (params: PointGroupsByIdsParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: [
        options?.customQueryKey ? options.customQueryKey : "point-groups",
        "by-ids",
        params,
      ],
      queryFn: ({ signal }) => fetchPointGroupsByIds(params, signal),
      ...options,
    }),
  match: (params: { companyId: number; name: string }) =>
    queryOptions({
      queryKey: ["get", "point-group-match", params],
      queryFn: () => fetchDoesPointGroupNameExist(params),
    }),
};

// Queries

export function usePointGroupsByIdSuspenseQuery(
  params: Omit<PointGroupsByIdsParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useSuspenseQueryDeferred(
    pointGroupQueries.byIds({ companyId, ...params }, options),
  );
}

export function usePointGroupsByIdQuery(
  params: Omit<PointGroupsByIdsParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointGroupQueries.byIds({ companyId, ...params }, options));
}

export function usePointGroupMatchQuery(name: string) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointGroupQueries.match({ companyId, name }));
}

export function usePointGroupsQuery(
  params: Omit<PointGroupListParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(pointGroupQueries.list({ ...params, companyId }, options));
}

// Fetchers

export async function fetchPointGroups(
  { companyId, ...params }: PointGroupListParams,
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<ApiResponsePaginated<PointGroupResponse[]>>(
      `analytics/companies/${companyId}/pointgroups`,
      {
        params,
        signal,
      },
    )
    .then((resp) => ({
      groups: resp.data.data.map((d) => new PointGroup(d)),
      pagination: resp.data.meta.pagination,
    }));
}

export async function fetchPointGroupsByIds(
  { companyId, ...params }: PointGroupsByIdsParams,
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<ApiResponsePaginated<PointGroupResponse[]>>(
      `analytics/companies/${companyId}/pointgroups`,
      {
        params: {
          page: 1,
          pageSize: params.groupIds.length,
          groupIds: params.groupIds,
          withValues: params.withValues,
        },
        signal,
      },
    )
    .then((resp) => ({
      groups: resp.data.data.map((d) => new PointGroup(d)),
      pagination: resp.data.meta.pagination,
    }));
}

export async function fetchDoesPointGroupNameExist(params: {
  companyId: number;
  name: string;
}) {
  return assetsApiRequest
    .get<
      ApiResponse<boolean>
    >(`analytics/companies/${params.companyId}/pointgroups/match`, { params: { name: params.name } })
    .then((resp) => resp.data.data);
}
